// ─── ⭐⭐ UN CAMPO CHE FA GIORNI SUOI, E GIORNI IN COMUNE ────────────────────
//
// 🗣️ Sua, 18/09/2026: *«poter differenziare le fasce orarie per campi: per
// esempio io ho quattro campi, voglio fare per tre campi le stesse fasce orarie
// e su un campo cambiare l'inizio delle fasce orarie»*. E alla domanda se un
// campo possa avere insieme giorni in comune e giorni suoi: *«sì»*.
//
// 🚨⭐⭐ **LA COSA DA SAPERE PRIMA DI TOCCARE QUESTA SCHERMATA, ed è misurata,
// non temuta.** La fascia dedicata a un campo **non si somma** a quelle
// generali: le **sostituisce**, e per il GIORNO INTERO. Lo fa `fasceDelGiorno`
// (⇒ `griglia.ts`), ed è la scelta giusta — due griglie sovrapposte darebbero
// due passi diversi per la stessa ora — ma ha una conseguenza feroce:
//
//   lunedì, Campo 4, prima:  08:00–12:00 · 12:00–18:00 · 18:00–23:00 → 11 ore
//   si aggiunge UNA fascia sua 09:00–12:00
//   lunedì, Campo 4, dopo:   09:00–12:00                             →  3 ore
//
// ⇒ Chi crea una fascia per un campo **gli spegne il resto della giornata**, e
//   nessun errore lo dice: i soci semplicemente non trovano più quelle ore.
// 📌 *Una sostituzione che sembra un'aggiunta è la forma peggiore di un
//    difetto: fa esattamente quello che ha promesso, a qualcos'altro.*
//
// ⇒ Da cui il mestiere di questo file: dare alla schermata le due parole che le
//   servono per non farlo capitare — quali giorni un campo ha **suoi**, e com'è
//   fatto il giorno **in comune** da copiargli dentro quando si stacca.
import { ORDINE_SETTIMANA } from './circolo.js';
/**
 * Le fasce che questa schermata governa: di **tutti i campi o di uno**, mai con
 * un periodo di validità.
 *
 * ⚠️ Le fasce con le date restano fuori di proposito, come già fuori dal foglio
 * (⇒ `laDisegnaIlFoglio`): governano il calendario solo in una finestra, e
 * mescolarle a quelle di sempre farebbe leggere come permanente una cosa che
 * scade. Continuano a vivere nell'elenco «le fasce che il foglio non disegna».
 */
function senzaDate(f) {
    return f.validFrom === null && f.validTo === null;
}
/** Le fasce **proprie** di un campo: quelle che portano il suo nome. */
export function fasceDelCampo(fasce, courtId) {
    return fasce.filter((f) => f.courtId === courtId && senzaDate(f));
}
/** Le fasce **in comune**: quelle che valgono per tutti i campi. */
export function fasceInComune(fasce) {
    return fasce.filter((f) => f.courtId === null && senzaDate(f));
}
/**
 * I giorni in cui questo campo **fa da sé**.
 *
 * ⚖️ Basta UNA fascia propria perché il giorno sia suo: non è una soglia
 * scelta da noi, è come legge `fasceDelGiorno`. Contarne due sarebbe
 * descrivere una regola diversa da quella che il calendario applica.
 * ⚠️ E conta anche una fascia **spenta**: una fascia spenta è pur sempre una
 * riga con quel `court_id`, e la sostituzione la fa la riga, non il suo stato.
 * 📌 *Chi deve prevedere un effetto guarda ciò che lo scatena, non ciò che è
 *    visibile.*
 */
export function giorniSuoi(fasce, courtId) {
    const suoi = new Set(fasceDelCampo(fasce, courtId).map((f) => f.weekday));
    return ORDINE_SETTIMANA.filter((g) => suoi.has(g));
}
/** `true` se quel giorno, su quel campo, vale ciò che vale per tutti. */
export function giornoInComune(fasce, courtId, weekday) {
    return !fasceDelCampo(fasce, courtId).some((f) => f.weekday === weekday);
}
/**
 * ⭐⭐ **Le fasce che un campo APPLICA DAVVERO**, giorno per giorno: le sue dove
 * ce l'ha, quelle di tutti dove non ce l'ha.
 *
 * 🎯 È la stessa regola di `fasceDelGiorno`, ma **per la settimana e senza una
 * data**: là serve a dire cosa può prenotare un socio quel martedì, qui a
 * disegnare il foglio di un campo. ⚠️ Sono due funzioni e non una perché la
 * prima deve pesare anche i periodi di validità, che qui sono esclusi per
 * scelta — e una funzione che serve due padroni finisce per servirne nessuno.
 */
export function fasceEfficaci(fasce, courtId) {
    const comuni = fasceInComune(fasce);
    if (courtId === null)
        return comuni;
    const proprie = fasceDelCampo(fasce, courtId);
    const giorniProprii = new Set(proprie.map((f) => f.weekday));
    return [...proprie, ...comuni.filter((f) => !giorniProprii.has(f.weekday))];
}
/**
 * ⭐⭐ **Cosa si scrive addosso a un campo per STACCARGLI un giorno senza
 * cambiargli niente.**
 *
 * 🚨 È la cura del difetto in testa al file, ed è tutta qui: staccare un giorno
 * **copiandoci dentro** quello in comune fa sì che, nell'istante dello stacco,
 * il campo veda **esattamente** quello che vedeva prima. Da lì si modifica.
 * ⛔ Senza questa copia, «dai una fascia al Campo 4» vuol dire «togli al Campo 4
 * tutte le altre ore di quel giorno», e lo scopre solo un socio che non trova
 * più il campo libero.
 * 📌 *Un'operazione che cambia la struttura non deve cambiare anche il
 *    contenuto: una cosa per volta, o non si sa quale delle due ha rotto.*
 */
export function giornoDaCopiare(fasce, weekday) {
    return fasceInComune(fasce).filter((f) => f.weekday === weekday);
}
//# sourceMappingURL=fasce-per-campo.js.map